import rawEmojiRendering from './emoji-rendering.json';

/** Minimal emoji metadata used to render glyphs without loading the full catalog. */
export interface EmojiRenderingDef {
    /** Human-readable label used for accessible fallbacks. */
    name: string;

    /**
     * Fully-qualified UTF-8 sequence when the renderable glyph differs from the base emoji code.
     */
    utf8?: string;
}

/** Lookup table keyed by emoji code for lightweight rendering and labeling. */
export const emojiRenderingByCode: Record<string, EmojiRenderingDef> = rawEmojiRendering.emojis;

/** Lookup table keyed by emoji code for lightweight markdown formatting. */
export const emojiShortnamesByCode: Record<string, string> = rawEmojiRendering.shortnames;

/** Lookup table keyed by emoji shortname for lightweight markdown parsing. */
export const emojiRenderingByShortname: Record<string, EmojiRenderingDef & { code: string }> =
    Object.entries({
        ...Object.fromEntries(
            Object.entries(emojiShortnamesByCode).map(([code, shortname]) => [shortname, code])
        ),
        ...rawEmojiRendering.shortnameAliases,
    }).reduce(
        (result, [shortname, code]) => {
            const definition = emojiRenderingByCode[code];
            if (definition) {
                result[shortname] = { ...definition, code };
            }

            return result;
        },
        {} as Record<string, EmojiRenderingDef & { code: string }>
    );

/**
 * Convert a stored emoji code into a Unicode glyph without depending on the full emoji catalog.
 */
export function getEmojiForCode(input: string): string {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return '';
    }

    const emoji = emojiRenderingByCode[code];
    if (!emoji) {
        return '';
    }

    const unicode = emoji.utf8 ?? code;
    const codePoints = unicode.split('-').map((entry) => Number.parseInt(entry, 16));
    return String.fromCodePoint(...codePoints);
}

/**
 * Resolve an emoji code to its label so render-only consumers can keep accessible text.
 */
export function getEmojiLabelForCode(input: string): string | null {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return null;
    }

    return emojiRenderingByCode[code]?.name ?? null;
}

/**
 * Resolve an emoji code to its canonical markdown shortname.
 */
export function getEmojiShortnameForCode(input: string): string | null {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return null;
    }

    return emojiShortnamesByCode[code] ?? null;
}

/**
 * Resolve a markdown shortname such as `:+1:` to an emoji code.
 */
export function getEmojiCodeForShortname(input: string): string | null {
    return emojiRenderingByShortname[input]?.code ?? null;
}

/**
 * Check whether a value matches a known emoji code before attempting to render it.
 */
export function isEmojiCode(input: string): boolean {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return false;
    }

    return Boolean(emojiRenderingByCode[code]);
}

/**
 * Normalize an emoji code or character to the stored emoji code.
 */
export function getEmojiCode(input: string): string | null {
    const code = normalizeEmojiCode(input);
    if (!code) {
        return null;
    }

    if (emojiRenderingByCode[code]) {
        return code;
    }

    return getEmojiCodeForSequence(emojiUnicode(code, '-'));
}

/**
 * Extract the leading emoji from a title while keeping the title text clean.
 */
export function extractEmojiFromTitle(text: string): { title: string; emoji?: string } {
    const parts = text.trim().split(' ');
    const emoji = parts[0] ? getEmojiCode(parts[0]) : null;

    if (emoji) {
        return {
            title: parts.slice(1).join(' '),
            emoji,
        };
    }

    return {
        title: parts.join(' '),
    };
}

let reverseEmojiSequences: null | Record<string, string> = null;

/**
 * Resolve a complete UTF-8 sequence to the stored emoji code.
 */
export function getEmojiCodeForSequence(unicode: string): string | null {
    if (emojiRenderingByCode[unicode]) {
        return unicode;
    }

    if (!reverseEmojiSequences) {
        reverseEmojiSequences = Object.entries(emojiRenderingByCode).reduce(
            (result, [code, emoji]) => {
                if (emoji.utf8) {
                    result[emoji.utf8] = code;
                }

                return result;
            },
            {} as Record<string, string>
        );
    }

    return reverseEmojiSequences[unicode] ?? null;
}

function normalizeEmojiCode(code: string): string {
    return code.toLowerCase();
}

function emojiUnicode(input: string, delimiter = ' '): string {
    return emojiToRawUnicode(input)
        .map((val) => val.toString(16))
        .join(delimiter);
}

function emojiToRawUnicode(input: string): number[] {
    if (input.length === 1) {
        return [input.charCodeAt(0)];
    }

    if (input.length > 1) {
        const pairs: number[] = [];
        for (let i = 0; i < input.length; i++) {
            const charCode = input.charCodeAt(i);
            if (charCode >= 0xd800 && charCode <= 0xdbff) {
                const nextCharCode = input.charCodeAt(i + 1);
                if (nextCharCode >= 0xdc00 && nextCharCode <= 0xdfff) {
                    pairs.push((charCode - 0xd800) * 0x400 + (nextCharCode - 0xdc00) + 0x10000);
                }
            } else if (charCode < 0xd800 || charCode > 0xdfff) {
                pairs.push(charCode);
            }
        }
        return pairs;
    }

    return [];
}
